/**
 * 商家資訊卡內「目前方案」區塊（SSOT）
 * Group（/lite）與 Square（/lite/shop/:suffix）共用，顯示訂閱方案、本月發佈、試用期／預計更新、取消訂閱
 */

import type { SubscriptionStatus } from '../api/liteSubscriptionClient';
import { formatDateTaipeiSlash } from '@pos/shared/utils/time';
import { Button } from '@/components/ui/button';

/** 方案顯示名稱（與訂閱頁、後端 slug 一致） */
export const LITE_PLAN_NAMES: Record<string, string> = {
  free: '未訂閱',
  trial: '試用期',
  basic: '一般方案',
  plus: '加值方案',
  pro: '專業方案',
};

export interface MerchantSubscriptionBlockProps {
  /** 是否載入中 */
  loading: boolean;
  /** 訂閱狀態（null 表示尚未載入或無資料，不顯示區塊） */
  subStatus: SubscriptionStatus | null;
  /** 點擊「前往訂閱」或「立即訂閱」時導向訂閱頁 */
  onGoToSubscription: () => void;
  /** 點擊「取消訂閱」時（付費方案且未設不續訂時顯示），選填 */
  onOpenCancel?: () => void;
}

/**
 * 單一元件：商家主畫面內「目前方案」的顯示與操作
 * 使用於 MainPage（Group）與 SquareLandingPage（Square），確保兩處顯示一致
 */
export function MerchantSubscriptionBlock({
  loading,
  subStatus,
  onGoToSubscription,
  onOpenCancel,
}: MerchantSubscriptionBlockProps) {
  if (!loading && !subStatus) return null;

  return (
    <div className="rounded-lg border-2 border-blue-100 bg-blue-50/50 p-4">
      {loading ? (
        <p className="text-muted-foreground">載入中...</p>
      ) : subStatus ? (
        <div className="space-y-1">
          <p className="font-medium text-gray-900 flex items-center justify-between gap-2 w-full">
            <span className="flex items-center gap-2">
              <span>目前方案：</span>
              <span>
                {LITE_PLAN_NAMES[subStatus.planSlug] ?? subStatus.planSlug}
              </span>
            </span>
            {subStatus.planSlug === 'free' ? (
              <Button variant="default" size="sm" onClick={onGoToSubscription}>
                前往訂閱
              </Button>
            ) : (
              !subStatus.cancelAtPeriodEnd &&
              subStatus.planSlug !== 'trial' &&
              onOpenCancel && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs border-gray-300 shrink-0"
                  onClick={onOpenCancel}
                >
                  取消訂閱
                </Button>
              )
            )}
          </p>
          {subStatus.planSlug !== 'free' && (
            <p className="text-gray-700">
              本月發佈 {subStatus.used}/
              {subStatus.limit >= 999 ? '∞' : subStatus.limit}
            </p>
          )}
          {subStatus.planSlug === 'trial' && subStatus.trialEndsAt && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <div className="flex items-center justify-between gap-2">
                <span>
                  試用期至 {formatDateTaipeiSlash(subStatus.trialEndsAt)} 到期
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onGoToSubscription}
                >
                  立即訂閱
                </Button>
              </div>
            </div>
          )}
          {subStatus.planSlug !== 'trial' &&
            subStatus.planSlug !== 'free' &&
            subStatus.periodEnd && (
              <p className="text-gray-600">
                預計 {formatDateTaipeiSlash(subStatus.periodEnd)} 更新
              </p>
            )}
          {subStatus.cancelAtPeriodEnd && (
            <p className="text-amber-700 text-xs mt-1.5">
              已設定不續訂，本期結束後將不再扣款
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
